import { css } from 'aphrodite';
import { toast } from 'sonner';
import { VerbalConsentScripts } from '../../../../extViewmodels/sdk/models/Telephony';
import { copyToClipboard } from '../../../../models/UiUtils';
import { Button } from '../../../components-v2/Button';
import { Skeleton } from '../../../components-v2/Skeleton';
import { ClipboardIcon2 } from '../../../components/svgs/icons/ClipboardIcon';
import { styleSheet } from './styles';

async function copyText(value: string) {
	if (await copyToClipboard(value)) {
		toast.success('Copied to clipboard');
	} else {
		toast.error('Failed to copy to clipboard');
	}
}

function CopyButton({ value, label, disabled }: { value: string; label: string; disabled: boolean }) {
	return (
		<Button
			type='button'
			variant='outline'
			size='sm'
			aria-label={label}
			disabled={disabled}
			onClick={() => copyText(value)}
		>
			<ClipboardIcon2 width={16} height={16} />
			Copy
		</Button>
	);
}

export function TextingRegistrationVerbalConsentScripts({
	scripts,
	isAwaitingBusinessName,
	isAwaitingUseCase,
	isLoading,
	isUpdating,
	isError,
}: {
	scripts?: VerbalConsentScripts;
	isAwaitingBusinessName: boolean;
	isAwaitingUseCase: boolean;
	isLoading: boolean;
	isUpdating: boolean;
	isError: boolean;
}) {
	const renderContent = () => {
		if (isAwaitingBusinessName) {
			return (
				<p className={css(styleSheet.verbalScriptNote)}>
					Add your legal business name in the previous step to see your script.
				</p>
			);
		}
		if (isAwaitingUseCase) {
			return <p className={css(styleSheet.verbalScriptNote)}>Choose a use case to see your script.</p>;
		}
		if (!scripts) {
			if (isError) {
				return (
					<p className={css(styleSheet.verbalScriptError)} role='alert'>
						We couldn&apos;t generate the script. Check the business name and privacy policy URL.
					</p>
				);
			}
			return isLoading ? <Skeleton styles={[styleSheet.verbalScriptSkeleton]} /> : null;
		}
		return (
			<>
				<div className={css(styleSheet.verbalScriptStep)}>
					<div className={css(styleSheet.verbalScriptStepHeader)}>
						<h3 className={css(styleSheet.verbalScriptHeading)}>Verbal script</h3>
						<CopyButton value={scripts.script} label='Copy script' disabled={isUpdating} />
					</div>
					<p className={css(styleSheet.verbalScriptText)}>{scripts.script}</p>
				</div>
				<div className={css(styleSheet.verbalScriptStep)}>
					<div className={css(styleSheet.verbalScriptStepHeader)}>
						<h3 className={css(styleSheet.verbalScriptHeading)}>
							After they agree, text them this from their conversation
						</h3>
						<CopyButton value={scripts.confirmationText} label='Copy confirmation text' disabled={isUpdating} />
					</div>
					<p className={css(styleSheet.verbalScriptText)}>{scripts.confirmationText}</p>
				</div>
			</>
		);
	};
	return (
		<section
			className={css(styleSheet.verbalScriptPanel, isUpdating && styleSheet.verbalScriptPanelUpdating)}
			aria-label='Verbal opt-in script'
			aria-busy={isUpdating}
		>
			{renderContent()}
		</section>
	);
}
